"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { StepperProvider } from "@ocr-monorepo/ui/components/stepper";
import { StepIndicator } from "@/components/onboarding/step-indicator";
import { SignupCompletionTracker } from "@/components/onboarding/signup-completion-tracker";
import { captureGrowthEvent } from "@/lib/analytics";

const steps = [
  { id: "create-account", title: "Account", path: "/getting-started/create-account" },
  { id: "verify-email", title: "Verify email", path: "/getting-started/verify-email" },
  { id: "plan", title: "Plan & payment", path: "/getting-started/plan" },
];

function getActiveIndex(pathname: string) {
  if (pathname.includes("/complete")) return -1;
  const index = steps.findIndex((step) => pathname.startsWith(step.path));
  return index === -1 ? 0 : index;
}

function LayoutContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const activeIndex = getActiveIndex(pathname);
  const currentStep = activeIndex === -1 ? "complete" : steps[activeIndex].id;

  useEffect(() => {
    captureGrowthEvent("onboarding_step_viewed", {
      step: currentStep,
      step_number: activeIndex === -1 ? steps.length + 1 : activeIndex + 1,
    });
  }, [activeIndex, currentStep]);

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <SignupCompletionTracker />
      <header className="border-b border-border">
        <div className="mx-auto grid h-14 w-full max-w-[1440px] grid-cols-[1fr_auto] items-center px-4 min-[360px]:px-5 md:h-16 md:grid-cols-[1fr_auto_1fr] md:px-10 xl:px-16">
          <Link
            href="/"
            className="w-fit text-2xl font-bold tracking-[-0.06em] text-foreground md:text-[26px]"
            aria-label="PdfParse home"
          >
            pdfparse
          </Link>

          {activeIndex >= 0 && (
            <div className="hidden w-[520px] md:block">
              <StepIndicator activeIndex={activeIndex} steps={steps} />
            </div>
          )}

          {activeIndex === 0 ? (
            <Link
              href="/login"
              className="justify-self-end text-sm font-medium text-primary underline-offset-4 hover:underline md:col-start-3"
            >
              Sign in
            </Link>
          ) : (
            <span className="hidden md:col-start-3 md:block" aria-hidden="true" />
          )}
        </div>
      </header>

      <main className="w-full flex-1 px-4 pb-10 pt-5 min-[360px]:px-5 md:px-6 md:pb-14 md:pt-8">
        {children}
      </main>
    </div>
  );
}

export default function GettingStartedLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <StepperProvider orientation="vertical" className="contents">
      <LayoutContent>{children}</LayoutContent>
    </StepperProvider>
  );
}
